import React, { createContext } from "react";
import { IAttachmentContext, IAttachmentContextProviderProps } from ".";
import { FileProcessingStatus, IAttachmentFile } from "../../containers/Steps/Attachments";

export const AttachmentContext = createContext<IAttachmentContext>({
    Attachments: [],
    RemoveAttachment: (files: IAttachmentFile) => new Promise<any>(() => undefined),
    UploadAttachments: (files: IAttachmentFile[]) => new Promise<any>(() => undefined),
    IsAttachmentsFinished: true,

    SetInitialAttachments: (() => { return; }),
});

export interface IAttachmentContextProviderState {
    Attachments: IAttachmentFile[];
    IsAttachmentsFinished: boolean;
}

interface IFileResult {
    success: boolean;
    details?: string;
    message?: string;
}

export default class AttachmentContextProvider extends React.Component<IAttachmentContextProviderProps, IAttachmentContextProviderState> {

    constructor(props: IAttachmentContextProviderProps) {
        super(props);
        this.state = {
            Attachments: props.InitialAttachments,
            IsAttachmentsFinished: true,
        };
    }

    public componentDidUpdate(prevProps: Readonly<IAttachmentContextProviderProps>, prevState: Readonly<IAttachmentContextProviderState>) {
        // Props
        if (this.props.InitialAttachments.length &&
            this.props.InitialAttachments.every((item) => !prevProps.InitialAttachments.find((prevItem) => prevItem.File.name === item.File.name))
        ) {
            this.setState(() => ({
                Attachments: this.props.InitialAttachments,
            }));
        }

        // State
        const pendingAttachments = this.state.Attachments.filter((a) => a.ProcessingStatus === FileProcessingStatus.Pending);
        const deletingAttachments = this.state.Attachments.filter((a) => a.ProcessingStatus === FileProcessingStatus.Deleting);
        const uploadingAttachments = this.state.Attachments.filter((a) => a.ProcessingStatus === FileProcessingStatus.Uploading);

        if (prevState.Attachments !== this.state.Attachments) {
            this.setState(() => ({
                IsAttachmentsFinished: !uploadingAttachments.length
                    && !deletingAttachments.length
                    && !pendingAttachments.length,
            }));
        }

        if (pendingAttachments.length) {
            this.uploadAttachments(pendingAttachments);
        }

        if (deletingAttachments.length) {
            this.deleteAttachments(deletingAttachments);
        }
    }

    public render = () => {
        return (
            <AttachmentContext.Provider value={{
                Attachments: this.state.Attachments,
                RemoveAttachment: this.removeAttachment,
                UploadAttachments: this.addAttachments,
                IsAttachmentsFinished: this.state.IsAttachmentsFinished,
                SetInitialAttachments: this.setInitialAttachments,
            }}>
                {this.props.children}
            </AttachmentContext.Provider >
        );
    }

    private setInitialAttachments = (files: IAttachmentFile[]) => {
        this.setState({
            Attachments: files,
        });
    }

    private addAttachments = (files: IAttachmentFile[]) => {
        this.setState({
            Attachments: this.state.Attachments.concat(files).sort((a, b) => {
                if (a.File.name.toLowerCase() < b.File.name.toLowerCase()) {
                    return -1;
                }
                if (a.File.name.toLowerCase() > b.File.name.toLowerCase()) {
                    return 1;
                }
                return 0;
            }),
        });
    }

    private removeAttachment = (file: IAttachmentFile) => {
        this.setState({
            Attachments: this.state.Attachments.map((a) => {
                if (file.File.name === a.File.name) {
                    return { ...a, ProcessingStatus: FileProcessingStatus.Deleting };
                }
                return a;
            }),
        });
    }

    private uploadAttachments = async (files: IAttachmentFile[]) => {
        const updatedAttachments = this.state.Attachments.map((a) => {
            if (files.filter((file) => this.matchFile(file, a, FileProcessingStatus.Pending)).length) {
                return { ...a, ProcessingStatus: FileProcessingStatus.Uploading };
            }
            return a;
        });

        this.setState({
            Attachments: updatedAttachments,
        }, (async () => {
            for (const file of files) {
                await this.performUpdate(file);
            }
        }));
    }

    private deleteAttachments = async (files: IAttachmentFile[]) => {
        for (const file of files) {
            await this.performDelete(file);
        }
    }

    private performUpdate = async (file: IAttachmentFile) => {
        await this.props.AttachmentEvents.uploadEmailAttachment(this.props.AttachmentFolder, file.File)
            .then(async (uploadResult: IFileResult) => {
                this.setUploadAttachmentResponse(file, uploadResult, () => {
                    return;
                });
            }).catch(() => {
                this.rejectFileUpload(file, () => {
                    return;
                });
            });
    }

    private performDelete = async (file: IAttachmentFile) => {

        await this.props.AttachmentEvents.deleteEmailAttachment(file.File.name, this.props.AttachmentFolder)
            .then(async (deleteResult: IFileResult) => {
                this.setDeleteAttachmentResponse(file, deleteResult, () => {
                    return;
                });
            }).catch(() => {
                this.rejectFileDelete(file, () => {
                    return;
                });
            });
    }

    private setUploadAttachmentResponse = (file: IAttachmentFile, result: IFileResult, callback: () => void) => {
        const updatedAttachments = this.state.Attachments.map((a) => {
            if (this.matchFile(file, a, FileProcessingStatus.Uploading)) {
                return { ...a, ProcessingStatus: result ? FileProcessingStatus.Uploaded : FileProcessingStatus.Errored };
            }
            return a;
        });

        return this.setState({
            Attachments: updatedAttachments,
        }, callback);
    }

    private setDeleteAttachmentResponse = (file: IAttachmentFile, result: IFileResult, callback: () => void) => {
        if (result.success) {
            const updatedAttachments = this.state.Attachments.map((a) => {
                if (this.matchFile(file, a, FileProcessingStatus.Deleting)) {
                    return { ...a, ProcessingStatus: result ? FileProcessingStatus.Deleted : FileProcessingStatus.Errored };
                }
                return a;
            });

            this.setState({
                Attachments: updatedAttachments,
            }, callback);
        } else {
            callback();
        }
    }

    private rejectFileUpload = (file: IAttachmentFile, reject: (reason?: any) => void) => {
        const result = { success: false };
        this.setUploadAttachmentResponse(file, result, () => {
            reject(result);
        });
    }

    private rejectFileDelete = (file: IAttachmentFile, reject: (reason?: any) => void) => {
        const result = { success: false };
        this.setDeleteAttachmentResponse(file, result, () => {
            reject(result);
        });
    }

    private matchFile = (existingFile: IAttachmentFile, newFile: IAttachmentFile, processingStatus: FileProcessingStatus): boolean => {
        return existingFile.File.name === newFile.File.name &&
            newFile.ProcessingStatus === processingStatus;
    }
}
